// Hand-authored fallback table.
//
// Built once (CHG-1179 / ITER-3293) by extracting `value` + `label` + `description`
// from specs/seeds/lifecycle/{campaign,donor,pledge,recurring}/stages.yaml. Used by
// stage-labels.ts when CHG-1132's consolidated stage-labels.yaml hasn't merged yet
// (or has merged but the runtime hasn't been re-bundled). When CHG-1132 lands, a
// follow-up CHG should regenerate this from the consolidated YAML and swap the
// loader's primary source.
//
// All stage_ids are snake_case (matching the seed canonical form). Normalize at
// lookup site via normalizeStageId() — accepts kebab-case input.

export type Entity = "campaign" | "donor" | "pledge" | "recurring";

export interface StageSeed {
  label: string;
  description: string;
}

export const STAGE_LABELS_FALLBACK: Record<Entity, Record<string, StageSeed>> = {
  campaign: {
    launching:    { label: "Launching",   description: "Early window — low data while the classifier waits for enough timeline to judge pace." },
    active:       { label: "Active",      description: "Running normally; not yet clearly on track or off pace." },
    on_track:     { label: "On track",    description: "Pace at or above 90% of expected. Likely to hit goal." },
    at_risk:      { label: "At risk",     description: "Pace below 75% of expected. Will miss goal without acceleration." },
    off_pace:     { label: "Off pace",    description: "Pace below 50% of expected. Significant shortfall likely." },
    final_push:   { label: "Final push",  description: "Last 15% of timeline. Tone shifts to urgency + social proof." },
    complete:     { label: "Complete",    description: "Goal reached. Celebratory messaging." },
    post_close:   { label: "Post-close",  description: "End date passed. Stewardship / thank-you cadence." },
  },
  donor: {
    prospect:     { label: "Prospect",    description: "Donor record exists but no donation recorded." },
    first_time:   { label: "First-time",  description: "Exactly one lifetime donation recorded." },
    active:       { label: "Active",      description: "Gave within the at-risk threshold (default 180 days)." },
    at_risk:      { label: "At-risk",     description: "Past at-risk threshold but not lapsed yet." },
    lapsed:       { label: "Lapsed",      description: "Past lapsed threshold (default 365 days)." },
    reactivated:  { label: "Reactivated", description: "Was at-risk or lapsed; gave again within reactivation window." },
    recurring:    { label: "Recurring",   description: "Has at least one active recurring donation." },
    vip:          { label: "VIP",         description: "Lifetime giving + count meets VIP thresholds." },
  },
  pledge: {
    committed:       { label: "Committed",       description: "Pledge made; no payments yet; before due date." },
    active:          { label: "Active",          description: "First payment received; early in the fulfillment window." },
    fulfilling:      { label: "Fulfilling",      description: "Multiple payments received; below pace-classification thresholds." },
    on_track:        { label: "On track",        description: "Installment fulfillment ratio ≥ 90% of expected." },
    falling_behind:  { label: "Falling behind",  description: "Installment fulfillment 50–75% of expected." },
    at_risk:         { label: "At risk",         description: "Fulfillment < 50% of expected and due date within 14 days." },
    fulfilled:       { label: "Fulfilled",       description: "Pledge fully paid. Celebration + stewardship arc." },
    lapsed:          { label: "Lapsed",          description: "Due date passed without full payment." },
  },
  recurring: {
    new:                  { label: "New",                  description: "< 30 days old; no successful charges yet." },
    welcoming:            { label: "Welcoming",            description: "< 60 days old with at least one successful charge." },
    active:               { label: "Active",               description: "Healthy past the welcoming window; no failed retries." },
    long_standing:        { label: "Long-standing",        description: "> 365 days old, healthy. Anniversary + loyalty arc." },
    at_risk_churn:        { label: "At risk of churn",     description: "Retries failing OR last charge is past expected." },
    past_due:             { label: "Past due",             description: "All retries exhausted. Terminal unless payment method updated." },
    canceled:             { label: "Canceled",             description: "Donor or processor ended the subscription." },
    win_back_attempted:   { label: "Win-back attempted",   description: "Win-back message sent after cancellation." },
  },
};
